import React, { useEffect, useState, useRef } from "react";
import PageSeo from "../../components/seo/PageSeo";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import QRCode from "react-qr-code";

import { createDepositCall, depositStatusCall, balanceHistoryCall, depositCurrenciesCall } from "../../calls";
import { copyText } from "../../utils/clipboard";
import { fmtDate, fmtTime } from "../../utils/i18nFormat";
import { depositError, reasonLabel } from "../../utils/serverMessages";
import "./deposit.css";

/* DEPOSIT — FixedFloat (ff.io). Le client choisit un montant + une crypto,
   on crée un ordre, on affiche l'adresse de dépôt + QR, puis on interroge
   deposit_status jusqu'au crédit (ff.io n'a pas de webhook). */

/* Devises de depot — choix en deux temps : la monnaie, puis le reseau.

   ff.io accepte 68 codes, mais 68 tuiles c'est illisible et dangereux : on garde
   les monnaies reellement echangees (COINS) et on expose leurs reseaux dans une
   seconde ligne. Les codes de DENIED sont ecartes volontairement : ce sont des
   jetons enveloppes ou adosses (Wrapped ETH, "Bitcoin (BEP20)"...), pas l'actif
   natif — un client qui y envoie du vrai BTC ou du vrai ETH perd ses fonds.
   La liste reste alimentee par l'API : un nouveau reseau ajoute par ff.io sur
   une monnaie deja listee apparait tout seul, sans redeploiement. */
const COINS = ["USDT", "USDC", "BTC", "ETH", "BNB", "SOL", "XMR", "LTC", "TRX", "DOGE", "XRP", "ADA", "BCH"];
const DENIED = new Set([
  "WETHETH", "WETHARBITRUM", "WETHBASE", "WETHMATIC", "WBNBBSC", "WSOL",
  "BTCBSC", "ETHBSC",
]);
// Reseau propose par defaut quand on choisit la monnaie. On prend le reseau
// canonique (celui auquel un client s'attend), pas le moins cher : le minimum
// de chaque reseau est affiche, libre a lui de prendre l'option a 1,23 $.
const DEFAULT_NET = {
  USDT: "USDTTRC", USDC: "USDCSOL", ETH: "ETH", BNB: "BSC", BTC: "BTC", SOL: "SOL",
};
// Repli si /order/deposit_currencies est injoignable.
const FALLBACK = [
  { code: "USDTTRC", coin: "USDT", name: "Tether (TRC20)", network: "TRX" },
  { code: "USDT", coin: "USDT", name: "Tether (ERC20)", network: "ETH" },
  { code: "USDCSOL", coin: "USDC", name: "USD Coin (Solana)", network: "SOL" },
  { code: "USDCETH", coin: "USDC", name: "USD Coin (ERC20)", network: "ETH" },
  { code: "BTC", coin: "BTC", name: "Bitcoin", network: "BTC" },
  { code: "ETH", coin: "ETH", name: "Ethereum", network: "ETH" },
  { code: "BSC", coin: "BNB", name: "BNB Smart Chain (BEP20)", network: "BSC" },
  { code: "SOL", coin: "SOL", name: "Solana", network: "SOL" },
  { code: "XMR", coin: "XMR", name: "Monero", network: "XMR" },
  { code: "LTC", coin: "LTC", name: "Litecoin", network: "LTC" },
  { code: "TRX", coin: "TRX", name: "Tron", network: "TRX" },
  { code: "DOGE", coin: "DOGE", name: "Dogecoin", network: "DOGE" },
];

// "Tether (TRC20)" -> "TRC20" ; sinon on retombe sur le reseau brut ("TRX").
function networkLabel(c) {
  const m = /\(([^)]+)\)/.exec(c.name || "");
  return (m && m[1]) || c.network || "";
}

/** Regroupe les devises de l'API par monnaie, dans l'ordre de COINS. */
function groupByCoin(list) {
  const by = new Map(COINS.map((c) => [c, []]));
  list.forEach((c) => {
    if (!c || !c.code || DENIED.has(c.code)) return;
    const bucket = by.get(c.coin);
    if (bucket) bucket.push({ ...c, net: networkLabel(c) });
  });
  // Reseaux tries du moins cher au plus cher : le minimum est le seul critere
  // que le client peut comparer d'un coup d'oeil.
  by.forEach((nets) =>
    nets.sort((a, b) => (a.min_usd || 0) - (b.min_usd || 0) || a.net.localeCompare(b.net))
  );
  return COINS.map((coin) => ({ coin, nets: by.get(coin) || [] })).filter((g) => g.nets.length);
}

/** Code a selectionner quand on clique sur une monnaie. */
function defaultCodeFor(group) {
  const pref = DEFAULT_NET[group.coin];
  if (pref && group.nets.some((n) => n.code === pref)) return pref;
  return group.nets[0].code;
}

const PRESETS = [10, 25, 50, 100];
const MIN_DEPOSIT = 1;

// Rendu d'un mouvement de solde : libellé + couleur par type (kind du backend).
const KIND_META = {
  deposit: { label: "signal.kind-deposit", color: "var(--color-success, #0b7a5e)" },
  purchase: { label: "signal.kind-purchase", color: "var(--color-danger, #c8324a)" },
  refund: { label: "signal.kind-refund", color: "var(--color-primary, #1d4ed8)" },
  rent: { label: "signal.kind-rent", color: "var(--color-danger, #c8324a)" },
  manual: { label: "signal.kind-manual", color: "var(--color-muted)" },
  credit: { label: "signal.kind-credit", color: "var(--color-success, #0b7a5e)" },
  debit: { label: "signal.kind-debit", color: "var(--color-danger, #c8324a)" },
};
// Filtres : dépôts d'un côté, activité liée aux commandes de l'autre.
// Pas d'onglet « Tout » : l'historique s'ouvre sur les dépôts.
const HIST_FILTERS = [
  { key: "deposits", label: "signal.hist-deposits", kinds: ["deposit", "credit"] },
  { key: "orders", label: "signal.hist-orders", kinds: ["purchase", "refund", "rent"] },
];
// Historique paginé côté client, 20 lignes par page.
const HIST_PAGE_SIZE = 20;

const Deposit = (props) => {
  const { setIsLoggedIn, isLoggedIn, setLoader } = props;
  const { t, i18n } = useTranslation();

  const [method, setMethod] = useState("USDTTRC");
  // ff.io impose un minimum en CRYPTO (0.005 ETH ~ 12$) : le minimum en USD
  // depend donc de la devise choisie. On le recupere au chargement.
  const [minByCcy, setMinByCcy] = useState({});
  const [ccyList, setCcyList] = useState(FALLBACK);
  const [amount, setAmount] = useState("");
  const [error, setError] = useState([]);
  const [historyData, setHistoryData] = useState([]);
  const [order, setOrder] = useState(null);   // ordre de dépôt en cours (adresse, montant…)
  const [status, setStatus] = useState(null); // waiting / confirming / exchanging / finished / expired / emergency
  const [copied, setCopied] = useState(false);
  const [histFilter, setHistFilter] = useState("deposits");
  const [histPage, setHistPage] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef(null);
  const balance = localStorage.getItem("balance");

  const activeFilter = HIST_FILTERS.find((f) => f.key === histFilter) || HIST_FILTERS[0];
  const filteredHistory = activeFilter.kinds
    ? historyData.filter((it) => activeFilter.kinds.includes(it.kind))
    : historyData;
  // Page courante bornée : si l'historique recharge et raccourcit, on ne reste
  // pas sur une page vide.
  const pageCount = Math.max(1, Math.ceil(filteredHistory.length / HIST_PAGE_SIZE));
  const safePage = Math.min(Math.max(1, histPage), pageCount);
  const pageStart = (safePage - 1) * HIST_PAGE_SIZE;
  const pageRows = filteredHistory.slice(pageStart, pageStart + HIST_PAGE_SIZE);

  const loadHistory = () => {
    balanceHistoryCall()
      .then((response) => setHistoryData(Array.isArray(response.data) ? response.data : []))
      .catch((err) => {
        if (err.response && err.response.status === 401) {
          setIsLoggedIn(false);
          localStorage.removeItem("token");
        }
      });
  };

  useEffect(() => {
    setLoader(true);
    loadHistory();
    return () => pollRef.current && clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const STATUS_LABEL = {
    waiting: t("signal.dep-st-waiting"),
    confirming: t("signal.dep-st-confirming"),
    exchanging: t("signal.dep-st-exchanging"),
    finished: t("signal.dep-st-finished"),
    expired: t("signal.dep-st-expired"),
    emergency: t("signal.dep-st-emergency"),
    pending: t("signal.dep-st-waiting"),
    failed: t("signal.dep-st-failed"),
  };
  // "failed" : ff.io a rembourse ailleurs que sur notre wallet (ff_deposits.py) ;
  // sans lui, le suivi interrogeait le serveur indefiniment.
  const isTerminal = (s) => s === "finished" || s === "expired" || s === "failed";

  const startPolling = (orderId) => {
    pollRef.current && clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      depositStatusCall(orderId)
        .then((res) => {
          const d = res.data || {};
          setStatus(d.status);
          if (d.status === "finished") {
            if (d.credit_amount) {
              const bal = (Number(balance) || 0) + Number(d.credit_amount);
              localStorage.setItem("balance", String(bal));
            }
            loadHistory();
          }
          if (isTerminal(d.status)) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        })
        .catch(() => {});
    }, 5000);
  };

  useEffect(() => {
    let cancelled = false;
    depositCurrenciesCall()
      .then((res) => {
        if (cancelled) return;
        const list = (res.data && res.data.currencies) || [];
        const map = {};
        list.forEach((c) => {
          if (c.min_usd) map[c.code] = c.min_usd;
        });
        setMinByCcy(map);
        if (list.length) setCcyList(list);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // minimum applicable a la devise selectionnee (fallback sur le minimum du site)
  const minFor = (ccy) => Math.max(MIN_DEPOSIT, Math.ceil(minByCcy[ccy] || 0));
  const currentMin = minFor(method);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (submitting) return;
    setError([]);
    if (Number(amount) < currentMin) {
      setError([t("signal.dep-min-error", { min: currentMin })]);
      return;
    }
    setSubmitting(true);
    createDepositCall(amount, method)
      .then((response) => {
        setSubmitting(false);
        const d = response.data || {};
        setOrder(d);
        setStatus(d.status || "waiting");
        startPolling(d.order_id);
      })
      .catch((err) => {
        setSubmitting(false);
        if (err.response && err.response.status === 401) {
          setIsLoggedIn(false);
          localStorage.removeItem("token");
          return;
        }
        // Jamais le message brut du serveur (francais fixe ou texte ff.io) :
        // on le traduit d'apres son statut / son contenu.
        const coin = (ccyList.find((c) => c.code === method) || {}).coin || method;
        setError([depositError(err, t, { min: currentMin, coin })]);
      });
  };

  const reset = () => {
    pollRef.current && clearInterval(pollRef.current);
    pollRef.current = null;
    setOrder(null);
    setStatus(null);
    setAmount("");
    setError([]);
  };

  const copyAddress = () => {
    if (!order?.deposit_address) return;
    copyText(order.deposit_address).then((ok) => {
      if (!ok) return;
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  // Monnaies affichees, et reseaux de la monnaie selectionnee.
  const groups = groupByCoin(ccyList);
  const current = groups.find((g) => g.nets.some((n) => n.code === method)) || groups[0];
  const nets = current ? current.nets : [];

  const pickCoin = (group) => setMethod(defaultCodeFor(group));

  // Devise reellement utilisee par l'ordre en cours (pour rappeler le reseau).
  const sentCode = order && order.pay_currency;
  const sentRaw = sentCode && ccyList.find((c) => c.code === sentCode);
  const sentCcy = sentRaw ? { ...sentRaw, net: networkLabel(sentRaw) } : null;

  if (!isLoggedIn) return <Navigate replace to="/" />;

  return (
    <div className="sg-page">
      <PageSeo noindex title={t("signal.dep-title")} />
      {!order ? (
        /* ÉTAPE 1 — choix montant + crypto */
        <div className="sg-panel">
          <div className="sg-head">
            <h1 className="sg-h1">{t("signal.dep-title")}</h1>
            <span className="sg-right">
              <span style={{ font: "400 11px var(--font-body)", color: "var(--color-muted)", marginRight: 8 }}>{t("signal.dep-current")}</span>
              <span className="sg-amount">${Math.round((Number(balance) || 0) * 100) / 100}</span>
            </span>
          </div>

          {/* Etape A — la monnaie */}
          <div className="sg-kicker" style={{ marginBottom: 10 }}>{t("signal.dep-pay-with")}</div>
          <div className="sg-tiles sg-tiles--5" style={{ marginBottom: nets.length > 1 ? 14 : 18 }}>
            {groups.map((g) => {
              const sel = current && current.coin === g.coin;
              const code = sel ? method : defaultCodeFor(g);
              return (
                <button
                  key={g.coin}
                  type="button"
                  className={`sg-tile ${sel ? "is-on" : ""}`}
                  onClick={() => pickCoin(g)}
                >
                  <span className="sg-tile__t">{g.coin}</span>
                  <span className="sg-tile__s">
                    {g.nets.length > 1
                      ? t("signal.dep-net-count", { count: g.nets.length })
                      : g.nets[0].net}
                    {minByCcy[code] ? ` · ${t("signal.dep-min-short", { min: Math.ceil(minByCcy[code]) })}` : ""}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Etape B — le reseau, seulement si la monnaie en a plusieurs.
              Envoyer sur le mauvais reseau = fonds perdus, d'ou le choix explicite. */}
          {nets.length > 1 && (
            <>
              <div className="sg-boxhead" style={{ marginBottom: 8 }}>
                <span className="sg-kicker">{t("signal.dep-network")}</span>
                <span className="sg-boxhead__count">{current.coin}</span>
              </div>
              <div className="sg-chips" style={{ marginBottom: 18 }}>
                {nets.map((n) => (
                  <button
                    key={n.code}
                    type="button"
                    className={`sg-chip ${method === n.code ? "is-on" : ""}`}
                    onClick={() => setMethod(n.code)}
                    title={n.name}
                  >
                    <span className="sg-chip__t">{n.net}</span>
                    {n.min_usd ? (
                      <span className="sg-chip__s">{t("signal.dep-min-short", { min: Math.ceil(n.min_usd) })}</span>
                    ) : null}
                  </button>
                ))}
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div className="sg-field" style={{ flex: "1 1 200px" }}>
              <span className="sg-label">{t("signal.dep-amount")}</span>
              <div className="sg-inputrow">
                <span className="sg-inputrow__unit">USD</span>
                <input
                  required
                  inputMode="numeric"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}
                  placeholder="25"
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`sg-pager__b ${Number(amount) === p ? "is-on" : ""}`}
                  style={{ padding: "12px" }}
                  onClick={() => setAmount(String(p))}
                >
                  ${p}
                </button>
              ))}
            </div>
            <button type="submit" className="sg-btn sg-btn--primary" disabled={submitting}>
              {submitting ? <><span className="cr-spinner" /> {t("overview.proceed")}</> : t("overview.proceed")}
            </button>
          </form>

          {error.map((e, i) => (
            <p key={i} className="auth-card__error" style={{ marginTop: 12 }}>{e}</p>
          ))}
          <p className="sg-note">{t("signal.dep-min-note", { min: currentMin })}</p>
        </div>
      ) : (
        /* ÉTAPE 2 — envoyer le paiement + suivi */
        <div className="sg-panel">
          <div className="sg-head">
            <h1 className="sg-h1">{t("signal.dep-send-title")}</h1>
            <button type="button" className="sg-pager__b" onClick={reset}>{t("signal.dep-new")}</button>
          </div>

          <p className="sg-note" style={{ marginTop: 0, marginBottom: 12 }}>
            {t("signal.dep-send-note", { amount: order.credit_amount })}
          </p>

          {/* Le reseau est la seule erreur irrattrapable de cet ecran : envoyer
              sur une autre chaine que celle-ci, c'est perdre les fonds. */}
          {sentCcy && (
            <p className="sg-warn" style={{ marginBottom: 16 }}>
              {t("signal.dep-net-warn", {
                coin: sentCcy.coin || order.pay_currency,
                network: sentCcy.net || sentCcy.network,
              })}
            </p>
          )}

          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
            <div style={{ background: "#fff", padding: 12, borderRadius: 10 }}>
              {order.deposit_address ? (
                <QRCode value={order.deposit_address} size={168} />
              ) : null}
            </div>

            <div style={{ flex: "1 1 260px", display: "flex", flexDirection: "column", gap: 12 }}>
              <div className="sg-field">
                <span className="sg-label">{t("signal.dep-amount-send")}</span>
                <div className="sg-inputrow">
                  <span className="sg-inputrow__unit">{order.pay_currency}</span>
                  <input readOnly value={order.pay_amount || ""} />
                </div>
              </div>

              <div className="sg-field">
                <span className="sg-label">{t("signal.dep-to-address")}</span>
                <div className="sg-inputrow" style={{ cursor: "pointer" }} onClick={copyAddress}>
                  <input readOnly value={order.deposit_address || ""} style={{ font: "500 12px var(--font-mono, monospace)" }} />
                  <span className="sg-inputrow__unit">{copied ? t("signal.copied") : t("signal.copy")}</span>
                </div>
              </div>

              {order.deposit_tag ? (
                <div className="sg-field">
                  <span className="sg-label">{t("signal.dep-memo")}</span>
                  <div className="sg-inputrow">
                    <input readOnly value={order.deposit_tag} />
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* Suivi du statut */}
          <div style={{ marginTop: 18, padding: "12px 14px", borderRadius: 10, background: "var(--color-surface-2, #f4f6f9)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span
                style={{
                  width: 9, height: 9, borderRadius: "50%",
                  background: status === "finished" ? "var(--color-success, #0b7a5e)"
                    : status === "expired" || status === "failed" ? "var(--color-danger, #c8324a)"
                    : "var(--color-primary, #1d4ed8)",
                }}
              />
              <b>{STATUS_LABEL[status] || t("signal.dep-st-waiting")}</b>
            </div>
            {status === "finished" && (
              <p className="sg-note" style={{ margin: "8px 0 0" }}>
                {t("signal.dep-credited", { amount: order.credit_amount })}
              </p>
            )}
            {status === "expired" && (
              <p className="sg-note" style={{ margin: "8px 0 0" }}>
                {t("signal.dep-expired-body")}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Historique des mouvements de solde */}
      <div className="sg-panel">
        <div className="sg-head sg-head--center" style={{ marginBottom: 12 }}>
          <h2 className="sg-h2">{t("overview.balance-history")}</h2>
          <span className="sg-right" style={{ font: "400 12px var(--font-body)", color: "var(--color-muted)" }}>
            {filteredHistory.length} {t("overview.entries-total")}
          </span>
        </div>

        {/* Filtre : Dépôts / Commandes & remboursements */}
        <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
          {HIST_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`sg-pager__b ${histFilter === f.key ? "is-on" : ""}`}
              style={{ padding: "6px 12px" }}
              onClick={() => { setHistFilter(f.key); setHistPage(1); }}
            >
              {f.label.startsWith("signal.") ? t(f.label) : f.label}
            </button>
          ))}
        </div>

        <div className="sg-table" style={{ "--cols": "150px 120px 1fr 110px" }}>
          <div className="sg-tr sg-tr--head">
            <span>{t("overview.date")}</span>
            <span>{t("signal.hist-type")}</span>
            <span>{t("signal.hist-detail")}</span>
            <span className="sg-td--end">{t("overview.amount")}</span>
          </div>
          {filteredHistory.length === 0 ? (
            <div className="sg-empty" style={{ border: "none", borderRadius: 0 }}>{t("signal.no-entries")}</div>
          ) : (
            pageRows.map((item, index) => {
              const d = new Date(item.created_at);
              const amt = Number(item.amount);
              const positive = amt >= 0;
              const meta = KIND_META[item.kind] || { label: item.kind_label || item.kind || "—", color: "var(--color-muted)" };
              return (
                <div key={pageStart + index} className="sg-tr">
                  <span className="sg-td--mono" style={{ fontSize: 12 }}>
                    {fmtDate(d, undefined, i18n.language)} {fmtTime(d, undefined, i18n.language)}
                  </span>
                  <span>
                    <span
                      style={{
                        display: "inline-block", padding: "2px 8px", borderRadius: 999,
                        fontSize: 11, fontWeight: 600, color: meta.color,
                        background: "color-mix(in srgb, " + meta.color + " 14%, transparent)",
                      }}
                    >
                      {String(meta.label).startsWith("signal.") ? t(meta.label) : meta.label}
                    </span>
                  </span>
                  <span style={{ color: "var(--color-muted)", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {reasonLabel(item, t)}
                  </span>
                  <span
                    className="sg-td--end sg-price"
                    style={{ color: positive ? "var(--color-success)" : "var(--color-danger, #c8324a)" }}
                  >
                    {positive ? "+" : "−"}${Math.abs(amt).toFixed(2)}
                  </span>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination : seulement si l'onglet dépasse une page */}
        {filteredHistory.length > HIST_PAGE_SIZE && (
          <div className="sg-pager">
            <span className="sg-pager__info">
              {t("signal.hist-range", { from: pageStart + 1, to: pageStart + pageRows.length, total: filteredHistory.length })}
            </span>
            <div className="sg-pager__btns">
              <button
                type="button"
                className="sg-pager__b"
                disabled={safePage <= 1}
                onClick={() => setHistPage(safePage - 1)}
              >
                {t("signal.hist-prev")}
              </button>
              <button
                type="button"
                className="sg-pager__b"
                disabled={safePage >= pageCount}
                onClick={() => setHistPage(safePage + 1)}
              >
                {t("signal.hist-next")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Deposit;
